import { useEffect, useState } from "react";
import { ANALYTICS_ENABLED, isUntrackedPath } from "./config";
import { OPEN_CONSENT_EVENT, getConsent, setConsent } from "./consent";
import { getPageLanguage } from "./attribution";

/* ============================================================================
   Cookie banner for analytics (bottom of the screen).

   * Shown on public pages until the visitor chooses; hidden on /admin and /login.
   * "Accept" and "Decline" are equally prominent (required in the EU).
   * Footer links "Cookie settings" reopen it (openConsentSettings()).
   * Renders nothing on the server and on the first client render, so it never
     clashes with the prerendered HTML; it's position: fixed, so it doesn't
     shift the page layout (no CLS).
   ========================================================================== */

const TEXTS = {
  sv: {
    title: "Cookies för statistik",
    body: "Vi vill använda Google Analytics för att se hur webbplatsen används – till exempel vilka sidor som besöks och hur många som går vidare till App Store eller Google Play. Uppgifterna används inte för annonser. Du kan ändra ditt val när som helst under \"Cookie-inställningar\".",
    accept: "Godkänn",
    decline: "Avböj",
    privacy: "Integritetspolicy",
  },
  en: {
    title: "Cookies for statistics",
    body: "We'd like to use Google Analytics to see how the website is used – for example which pages are visited and how many people continue to the App Store or Google Play. The data isn't used for ads. You can change your choice any time under \"Cookie settings\".",
    accept: "Accept",
    decline: "Decline",
    privacy: "Privacy policy",
  },
  uk: {
    title: "Cookie для статистики",
    body: "Ми хочемо використовувати Google Analytics, щоб бачити, як користуються сайтом – наприклад, які сторінки відвідують і скільки людей переходять до App Store чи Google Play. Дані не використовуються для реклами. Змінити вибір можна будь-коли в «Налаштуваннях cookie».",
    accept: "Погодитися",
    decline: "Відхилити",
    privacy: "Політика конфіденційності",
  },
  ru: {
    title: "Cookie для статистики",
    body: "Мы хотим использовать Google Analytics, чтобы видеть, как используется сайт – например, какие страницы посещают и сколько людей переходят в App Store или Google Play. Данные не используются для рекламы. Изменить выбор можно в любой момент в «Настройках cookie».",
    accept: "Принять",
    decline: "Отклонить",
    privacy: "Политика конфиденциальности",
  },
};

/* Footer link label, per language (used by the site footers). */
export const COOKIE_SETTINGS_LABEL = {
  sv: "Cookie-inställningar",
  en: "Cookie settings",
  uk: "Налаштування cookie",
  ru: "Настройки cookie",
};

const CSS = `
.tk-consent { position: fixed; z-index: 1000; left: 1rem; right: 1rem; bottom: 1rem; max-width: 34rem; margin-inline: auto;
  padding: 1.1rem 1.2rem; border-radius: 1rem; background: #18181b; color: #fafafa; box-shadow: 0 20px 50px -15px rgba(0,0,0,.45);
  font-family: "Poppins", "Poppins Fallback", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; font-size: .9rem; line-height: 1.55; }
.tk-consent h2 { margin: 0 0 .35rem; font-size: 1rem; font-weight: 700; color: #fff; }
.tk-consent p { margin: 0; color: #d4d4d8; }
.tk-consent a { color: #fff; text-decoration: underline; text-underline-offset: 2px; }
.tk-consent-actions { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: .9rem; }
.tk-consent button { flex: 1 1 8rem; min-height: 2.75rem; padding: .6rem 1rem; border-radius: 999px; border: 1.5px solid #fafafa;
  background: transparent; color: #fafafa; font: inherit; font-weight: 700; cursor: pointer; }
.tk-consent button:hover { background: rgba(255,255,255,.1); }
.tk-consent button:focus-visible { outline: 2px solid #fbbf24; outline-offset: 2px; }
`;

export default function ConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!ANALYTICS_ENABLED || isUntrackedPath(window.location.pathname)) return undefined;
    if (getConsent() === null) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  if (!open) return null;

  const t = TEXTS[getPageLanguage()] || TEXTS.sv;
  const choose = (choice) => {
    setConsent(choice);
    setOpen(false);
  };

  return (
    <div className="tk-consent" role="dialog" aria-modal="false" aria-labelledby="tk-consent-title">
      <style>{CSS}</style>
      <h2 id="tk-consent-title">{t.title}</h2>
      <p>
        {t.body} <a href="/privacy">{t.privacy}</a>
      </p>
      <div className="tk-consent-actions">
        <button type="button" onClick={() => choose("denied")}>{t.decline}</button>
        <button type="button" onClick={() => choose("granted")}>{t.accept}</button>
      </div>
    </div>
  );
}
